import { MutualFundDetail } from './../../../models/mutualfund/trading-mutualfund.model';
import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, TemplateRef, SimpleChanges, Optional, Inject } from '@angular/core';

import { LogService } from '@pershing/pershing-core';

import { TradingEntitlements } from './../../../models/common/trading-entitlements';
import { Quote, Account, TradingOrderBase, SecurityType } from './../../../models/common/trading-common.model';
import { TradingService } from './../../../services/common/trading-service.service';

import { Subscription, BehaviorSubject } from 'rxjs';
import { Popover } from '../../common/popover/popover.service';
import { GetFundLoadTypeByIndication } from '../../../utils/trading-common';
import { openOrderProspectus } from '../../common/order-prospectus/open-order-prospectus-form';
import { MatDialog } from '@angular/material/dialog';
import { DecimalPipe } from '@angular/common';
import { registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';

@Component({
    selector: 'quote-component',
    templateUrl: './quote.component.html',
    styleUrls: ['./quote.component.css'],
    providers: [DecimalPipe]
})

/**
 * The Quote component to take care of rendering the quote section for all asset types.
 */
export class QuoteComponent implements OnInit, OnDestroy {

    @Input() platform: string;

    @Input() tradingEntitlements: TradingEntitlements;

    @Input() activeTheme: string;

    @Input() order: any;

    @Input() symbolStr: string;

    @Input() account: Account;

    @Input() isMobileDialog: boolean = false;

    @Input() quoteCategory: string;

    @Input() isNonDollarCCY: boolean;

    @Input() templateName: string;

    @Input() optionLegIndex: number;

    // MARKET for core market hours trading & EXTENDED for extended hours trading.
    selectedSession: string;

    @Output() quoteRefreshRequested: EventEmitter<any> = new EventEmitter<any>();
    showSpin: boolean = false;
    showRealTime: boolean = false;
    isRealTimeChecked: boolean = false;
    isExtendedHours: boolean = false;
    isHolidayWeekendHours: boolean = false;
    flagClassName = '';
    quoteSubscription: Subscription;
    quoteType: string;
    dollarSymbol: string = '$';
    crossMark: string = ' x ';
    showQuote: boolean = false;
    systemError: boolean = false;
    showQuoteUnavailableWarning: boolean = false;
    isCoreMarketHours: boolean = true;
    isEquity: boolean = false;
    isMutualFund: boolean = false;
    isMutualFundExchange: boolean = false;
    isOptionUnderlyingSymbol: boolean = false;
    isOption: boolean = false;
    isRefresh: boolean = false;
    errorKey: string;
    fsiMessage: string;
    quoteRequestInProgress: boolean = false;
    qtLastTrdExchDesc: string;
    qtBidExchDesc: string;
    qtAskExchDesc: string;
    sqtLastPrc: string;
    isNonDollar: boolean = false;
    isIndexSymbol: boolean = false;
    quote: Quote;
    fundDetails: MutualFundDetail;
    fundCutoffTime: Date;
    fundVestDerivedVal: string;
    shortSettlement: string;
    loadNoLoad: string;
    maxFrontEndLoadPctStr: string = '';
    deviceType: string = 'desktop';

    useComplexOptUnderlyingSymbolQuoteTpl: boolean = false;
    useOrigOptUnderlyingSymbolQuoteTpl: boolean = false;
    useComplexOptionSymbolQuoteTpl: boolean = false;
    useOrigOptionSymbolQuoteTpl: boolean = false;
    useComplexOptUnderlyingSymbolDescriptionTpl: boolean = false;
    language: string = '';

    constructor( private tradingService: TradingService, private logService: LogService, private popper: Popover, private dialog: MatDialog, private decimalPipe: DecimalPipe,
                 @Optional() @Inject('AppSettings') appSettings: any) {
                     if (appSettings?.language) {
                         this.language = appSettings.language;
                         if (this.language === 'es') {
                            registerLocaleData( es );
                         }
                     }
                     if (!!appSettings?.deviceType) {
                        this.deviceType = appSettings.deviceType;
                    }
    }

    ngOnInit() {
        this.setInitialValues();

        let quoteSubject$: BehaviorSubject<TradingOrderBase>;
        if (this.quoteCategory === 'MutualFundExchange') {
            quoteSubject$ = this.tradingService.secondQuoteSubject$;
        } else {
            quoteSubject$ = this.tradingService.quoteSubject$;
        }

        this.showQuote = false;

        if (this.quoteCategory === 'MutualFund' || this.quoteCategory === 'MutualFundExchange') {
          this.quoteSubscription = quoteSubject$.subscribe(
            (success: TradingOrderBase) => this.quoteSuccessCallbackForMutualFund(success),
            (error) => this.quoteErrorCallback(error));
        }

        this.showRealTimeCheckBox();
        this.tradingService.sessionTypeSubject$.subscribe((selectedSession: string) => this.onSessionTypeToggle(selectedSession));
    }

    private setInitialValues(): void {
        if (this.quoteCategory === 'Equity') {
            this.isEquity = true;
        } else if (this.quoteCategory === 'MutualFund') {
            this.isMutualFund = true;
        } else if (this.quoteCategory === 'MutualFundExchange') {
            this.isMutualFundExchange = true;
        } else if (this.quoteCategory === 'OptionUnderlyingSymbol') {
            this.isOptionUnderlyingSymbol = true;
            if (this.templateName === 'ComplexOptUnderlyingSymbolQuoteTpl') {
                this.useComplexOptUnderlyingSymbolQuoteTpl = true;
            } else if (this.templateName === 'ComplexOptUnderlyingSymbolDescriptionTpl') {
                this.useComplexOptUnderlyingSymbolDescriptionTpl = true;
            } else {
                this.useOrigOptUnderlyingSymbolQuoteTpl = true;
            }
        } else if (this.quoteCategory === 'Option') {
            this.isOption = true;
            if (this.templateName === 'ComplexOptionSymbolQuoteTpl') {
                this.useComplexOptionSymbolQuoteTpl = true;
            } else {
                this.useOrigOptionSymbolQuoteTpl = true;
            }
        }
    }

    ngOnChanges(changes: SimpleChanges) {
      if (!!this.symbolStr) {
        if (this.symbolStr !== '-') {
          this.fetchQuote();
        }
      } else {
        this.showQuote = false;
        this.systemError = false;
        this.showSpin = false;
      }
    }

    quoteSuccessCallbackForMutualFund(order: TradingOrderBase): void {
      if (!order) {
          this.showQuote = false;
          return;
      }
      if (this.quoteCategory === 'MutualFund') {
          this.quote = this.order.quote;
          this.fundDetails = this.order.mutualFund;
      } else if (this.quoteCategory === 'MutualFundExchange') {
          this.quote = this.order.exchangeQuote;
          this.fundDetails = this.order.exchangeMutualFund;
      }

      if (!!this.quote) {
          this.getQuoteHours();
          this.createFSIMessage();
          this.getCurrencyFlag();
          this.getTradeExchangeValue();
          this.systemError = false;
          this.showSpin = false;
          this.showQuote = true;
          this.logService.info('Got order object in Quote', order);
      } else {
          this.showQuote = false;
          this.showSpin = false;
      }
        this.processQuoteCallbackForMutualFund();
    }

    private processQuoteCallbackForMutualFund(): void {
        if ((this.isMutualFund || this.isMutualFundExchange) && this.fundDetails) {
            this.fundCutoffTime = new Date('1970-01-01T' + this.fundDetails.purchaseCutoffTime + '-05:00');

           this.setFundVestDerivedVal();

            this.shortSettlement = this.fundDetails.shortSettlementReasonCode === 'Y' ? 'Yes' : 'No';

            this.loadNoLoad = GetFundLoadTypeByIndication(this.fundDetails.loadNoLoadInd);

            if (this.fundDetails.maxFrontEndLoadPercentage) {
                this.maxFrontEndLoadPctStr = this.tradingEntitlements.isRetailUser ? Math.round(this.fundDetails.maxFrontEndLoadPercentage) + '%' :
                this.fundDetails.maxFrontEndLoadPercentage + '%';
            } else {
                this.maxFrontEndLoadPctStr = '';
            }
        }
    }

    private setFundVestDerivedVal(): void {
        const fundVestCode: string = this.fundDetails.ntfParticipationCode;
        if ( fundVestCode === 'R' || fundVestCode === 'I' || fundVestCode === 'O' ) {
            this.fundVestDerivedVal = 'yes';
        } else if ( fundVestCode === 'N' || fundVestCode === 'U' || fundVestCode === ' ' ) {
            this.fundVestDerivedVal = 'no';
        } else {
            this.fundVestDerivedVal = 'notApplicable';
        }
    }

    private getQuoteHours(): void {
        if (this.isCoreMarketHours) {
            this.isHolidayWeekendHours = false;
            this.isExtendedHours = false;
        } else {
            switch (this.quote.nyseOpenInd) {
                case '0': {
                    this.isHolidayWeekendHours = true;
                    this.isExtendedHours = false;
                    break;
                }
                case '1':
                case '3': {
                    this.isHolidayWeekendHours = false;
                    this.isExtendedHours = true;
                    break;
                }
                //default block includes when the value is 2
                default : {
                    this.isHolidayWeekendHours = false;
                    this.isExtendedHours = false;
                }
            }
        }
    }
    public fetchQuote(): void {
      if (!this.quoteRequestInProgress && (this.quoteCategory === 'Equity' || this.quoteCategory === 'OptionUnderlyingSymbol' || this.quoteCategory === 'Option')) {
          this.quoteRequestInProgress = true;
          this.showRealTimeCheckBox();
          this.tradingService.getQuote(this.platform, this.account.acctNum, this.symbolStr, this.quoteType, this.selectedSession, false).
              subscribe(
                  success => this.quoteSuccessCallbackForEquityOptions(success),
                  error => this.quoteErrorCallback(error)
              );
      }
    }

    quoteSuccessCallbackForEquityOptions(quoteResp): void {
        this.quoteRequestInProgress = false;
        this.showSpin = false;
        this.processQuoteCallbackForEquityOptions(quoteResp);
        if (!!this.quote) {
            this.getQuoteHours();
            this.createFSIMessage();
            this.getTradeExchangeValue();
            this.getCurrencyFlag();
            this.isIndexSymbol = this.quote.securityTypeEnum === SecurityType.INDEX ? true : false; 
            this.isRefresh = false;
            this.systemError = false;
            this.showSpin = false;
            this.showQuote = true;
            this.logService.info('Got order object in Quote', quoteResp);
        } else {
            this.showQuote = false;
            this.showSpin = false;
        }
        if(!!this.quote && this.quote.qtIntlInd === 'Y' && !!this.tradingEntitlements && !this.tradingEntitlements.isNonDollarOrderInstructionsEnabled){
            this.showQuote = false;
            this.showSpin = false;
        }
        this.updatePrincipal();
    }

    private processQuoteCallbackForEquityOptions(quoteResp): void {
        if (quoteResp.errorCode === 0 && quoteResp.response && quoteResp.response.returnCode === 'SUCCESS') {
            if (this.quoteCategory === 'Equity') {
                this.isEquity = true;
            } else if (this.quoteCategory === 'OptionUnderlyingSymbol') {
                this.isOptionUnderlyingSymbol = true;
            } else if (this.quoteCategory === 'Option') {
                this.isOption = true;
            }
            this.quote = quoteResp.response as Quote;
            this.showQuoteUnavailableWarning = false;
            this.quote.refresh = this.isRefresh;

            if (this.quoteCategory === 'Equity' || this.quoteCategory === 'Option' || this.quoteCategory === 'OptionUnderlyingSymbol') {
                 this.notifyQuoteSubscribers();
            }
        }
        /**
         * TEMPORARILY COMMENTING THIS OUT SO THAT A QUOTE_UNAVAILABLE RETURN CODE DOESN'T SHOW THE WARNING YET
         *  else if (quoteResp.errorCode === 0 && quoteResp.response && quoteResp.response.returnCode === 'QUOTE_UNAVAILABLE' && typeof this.optionLegIndex === 'number' ) {
         *    this.quote = quoteResp.response as Quote;
         *    this.quote.refresh = this.isRefresh;
         *    this.showQuoteUnavailableWarning = true;
         *    this.notifyQuoteSubscribers();
         *  }
        */
        else {
          this.showQuote = false;
          this.showQuoteUnavailableWarning = false;
          this.systemError = true;
          this.showSpin = false;
          this.errorKey = 'errors.HTTP_ERROR';
          if (typeof this.optionLegIndex === 'number') { // complex option leg quote
              this.quote = null;
              this.notifyQuoteSubscribers();
          }
          this.logService.info('Got error quote resp', quoteResp);
        }
    }

    quoteErrorCallback(error: any): void {
        this.quoteRequestInProgress = false;
        this.showQuote = false;
        this.systemError = true;
        this.showSpin = false;
        this.updatePrincipal();
        this.errorKey = 'errors.HTTP_ERROR';
        this.logService.info('Got order object in Quote', error);
    }


    showRealTimeCheckBox(): void {
        // User entitlement will always have isUnlimitedRTQuotesEnabled:true per new requirement
        // But for non-dollar quote is not realtime - use this.isRealTimeChecked to show delayed disclaimer
        if (this.isNonDollarCCY || (this.tradingEntitlements.isRetailUser && !this.tradingEntitlements.isExchangeAgreementSignedByUser)) {
          this.isRealTimeChecked = false;
          this.quoteType = 'DELAYED';
        } else {
          this.isRealTimeChecked = true;
          this.quoteType = 'REALTIME';
        }
        this.showRealTime = false;

        // **** Clean up following code later just in case we want to revert back the logic ****
        // if (!this.isExtendedHours && !this.isHolidayWeekendHours) {
        //     if (this.tradingEntitlements.isRTQuotesEnabled) {
        //         this.showRealTime = true;
        //         this.isRealTimeChecked = false;
        //     } else {
        //         this.showRealTime = false;
        //         this.isRealTimeChecked = false;
        //     }
        //     if (this.tradingEntitlements.isUnlimitedRTQuotesEnabled) {
        //         this.showRealTime = false;
        //         this.isRealTimeChecked = true;
        //         this.quoteType = 'REALTIME';
        //     }
        // } else {
        //     this.showRealTime = false;
        //     this.isRealTimeChecked = false;
        // }
    }

    showChartForSymbol(symbol: string): void {
        this.logService.info('Called showChartForSymbol with input symbol', symbol);
    }

    getCurrencyFlag() {
        let countryCD = (this.quote.qtCurrencyCd || '').trim().substring(0, 2).toLowerCase() || 'us';
        const classPrefix = 'flag-icon-';
        if (!!this.quote.symId) {
            const ccySuffix: string[] = this.quote.symId.trim().toLowerCase().split(':');
            if (ccySuffix.length > 1 && ccySuffix[1].length > 0) {
                countryCD = ccySuffix[1];
            }
        }
        this.flagClassName =  classPrefix + countryCD;
    }

    getTradeExchangeValue() {
        if (this.quote.sqtLastPrc !== null) {
            if (this.quote.sqtLastPrc < 1) {
                this.sqtLastPrc = this.decimalPipe.transform(this.quote.sqtLastPrc, '1.2-4', this.language === 'es' ? 'es' : '');
            } else {
                this.sqtLastPrc = this.decimalPipe.transform(this.quote.sqtLastPrc, '1.2-2', this.language === 'es' ? 'es' : '');
            }
        }

        if (this.quote.qtIntlInd !== null) {
            if (this.quote.qtIntlInd === 'Y') {
                this.isNonDollar = true;
                this.qtLastTrdExchDesc = '';
                this.qtBidExchDesc = '';
                this.qtAskExchDesc = '';
            } else if (this.quote.qtIntlInd === 'N') {
                this.isNonDollar = false;
                this.qtLastTrdExchDesc = this.quote.qtLastTrdExchDesc;
                this.qtBidExchDesc = this.quote.qtBidExchDesc;
                this.qtAskExchDesc = this.quote.qtAskExchDesc;
            } else {
                this.isNonDollar = false;
                this.qtLastTrdExchDesc = '';
                this.qtBidExchDesc = '';
                this.qtAskExchDesc = '';
            }
        }
    }

    createFSIMessage(): void {
        if (!!this.quote.eqtFsiInd) {
            this.fsiMessage = 'Financial Status Indicator: ';
            this.fsiMessage += this.quote.eqtFsiInd;
        }
        if (!!this.quote.eqtFsiDesc) {
            this.fsiMessage += ' - ';
            this.fsiMessage += this.quote.eqtFsiDesc;
        }
    }

    showDetails(content: TemplateRef<any>, origin) {
        this.popper.open({
            content,
            origin,
            width: '265px',
            classStr: 'margin-top-5'
        });
    }

    orderProspectus() {
      openOrderProspectus(this.account, this.quote, this.dialog, this.logService);
    }

    viewProspectus() {
      let clientId = this.tradingEntitlements.viewProspectusClientId;
      let cusipValue = this.quote.cusipId || this.fundDetails.cusip;

      let openUrl = this.tradingService.getViewPropectusLink() + `?clientid=${clientId}&fundid=${cusipValue}`;

      window.open(openUrl, '_blank');
    }

    refreshQuote(): void {
        this.quoteType = this.isRealTimeChecked ? 'REALTIME' : 'DELAYED';
        this.showSpin = true;
        this.isRefresh = true;
        if (this.quoteCategory === 'Equity' || this.quoteCategory === 'OptionUnderlyingSymbol' || this.quoteCategory === 'Option') {
          this.fetchQuote();
        } else {
            console.log('Updating principal on Quote refresh for - ' + this.quoteCategory);
            this.quoteRefreshRequested.emit({
                quoteType: this.quoteType,
                quoteCategory: this.quoteCategory
            });
        }
    }

    // rest the for based on the extended hours or core market hours
    private onSessionTypeToggle(selectedSession: string): void {
        this.selectedSession = selectedSession;

        if (selectedSession === 'EXTENDED') {
            this.isCoreMarketHours = false;
        } else {
            this.isCoreMarketHours = true;
        }
    }

    ngOnDestroy(): void {
        if ( !!this.quoteSubscription ) {
            this.quoteSubscription.unsubscribe();
        }
    }

    private notifyQuoteSubscribers(): void {
      if (typeof this.optionLegIndex === 'number') { // complex option leg quote
        const tempOrder: any = {
            optionLegIndex: this.optionLegIndex,
            quote: this.quote
        };

        this.tradingService.optionLegQuoteSubject$.next(tempOrder);

      } else {
        let tempOrder: any = this.tradingService.quoteSubject$.value ?? {};

        if (this.quoteCategory === 'OptionUnderlyingSymbol') {
          tempOrder.underlyingSymbolQuote = this.quote;
          if (!!tempOrder.quote) {
            tempOrder.quote.refresh = this.quote.refresh;
          }
        } else {
          tempOrder.quote = this.quote;
        }
        this.tradingService.quoteSubject$.next(tempOrder);
      }
    }

    refreshOptionLegsQuotes() {
        if (this.showSpin) {  // Call in progress
            return;
        }
        this.refreshQuote();
        this.quoteRefreshRequested.emit({
            refreshOtherLegQuote: true,
            thisLegIndex: this.optionLegIndex
        });
    }

    private updatePrincipal() {
        console.log('Updating principal on Quote refresh for - ' + this.quoteCategory);
        this.quoteRefreshRequested.emit({
            reCalculatePrincipal: true
        });
    }
}
